import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef, ChangeDetectorRef, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import flatpickr from 'flatpickr';
import { ViewTableComponent } from '../../base/view-table/view-table.component';
import { FormBuilder, NgForm } from '@angular/forms';
import { LocalizationService } from 'src/app/services/localization.service';
import { getTaxTypeShortLabel } from 'src/app/constants/tax.constants';
import { ActivatedRoute, Router } from '@angular/router';
import { UtilityService } from 'src/app/services/utility.service';
import { DialogService } from 'src/app/services/dialog.service';
import { EntityRESTService } from 'src/app/services/entity-rest.service';
import { FieldsService } from 'src/app/services/fields.service';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { TaxonomyService } from 'src/app/services/taxonomy.service';
import { AuthRESTService } from 'src/app/services/auth-rest.service';
import { AuthService } from 'src/app/services/auth.service';
import { MenuRESTService } from 'src/app/services/menu-rest.service';
import { RouteEventsService } from 'src/app/route-events.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { ConfigPagesService } from 'src/app/services/config-pages.service';
import { PackagesService } from 'src/app/services/packages.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserService } from 'src/app/services/user.service';
import { DomSanitizer } from '@angular/platform-browser';
import { PrintService } from 'src/app/services/print.service';
import { RefreshService } from 'src/app/services/refresh.service';
import { StudioConfigService } from 'src/app/services/studio-config.service';
import { ComponentRefreshService } from 'src/app/services/component-refresh.service';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyService } from '../../../services/currency.service';

@Component({
  selector: 'app-us-sales-tax-report',
  templateUrl: './us-sales-tax-report.component.html',
  styleUrls: ['./us-sales-tax-report.component.css']
})
export class UsSalesTaxReportComponent extends ViewTableComponent implements OnInit, AfterViewInit, OnDestroy {

  currentPage: number;

  baseColumns: string[] = [
    'title',
    'field_date_and_time',
    'field_gross_tuition',
    'field_payment_id',
    'field_payment_type',
    'field_status',
    'field_student_name',
    'field_tax_collected',
  ];
  override displayedColumns: string[] = [...this.baseColumns, 'field_total_payment_amount'];

  // All filters are applied server-side so the CSV export always matches the
  // screen. Empty / unset means "not filtered".
  taxTypeFilter: string[] = [];
  paymentTypeFilter: number[] = [];
  statusFilter: string[] = [];
  searchText: string = '';
  nonzeroTaxOnly: boolean = false;
  minAmount: number | null = null;
  maxAmount: number | null = null;

  availableTaxTypes: string[] = [];
  availablePaymentTypes: { id: number; name: string }[] = [];
  availableStatuses: string[] = [];
  // Per-type columns on screen/print/CSV: the selected tax types, or every
  // type present in the range when nothing is selected (server-decided).
  displayTypes: string[] = [];
  summary: {
    total_tax_collected: number;
    total_gross: number;
    total_payment_amount: number;
    type_totals: Record<string, number>;
    type_untracked: Record<string, number>;
  } | null = null;

  @ViewChild('dateRangeInput') dateRangeInput!: ElementRef;
  dateTimeRange: Date[] = [
    new Date(new Date().getFullYear(), 0, 1),
    new Date()
  ];
  dateRangeDisplayValue: string = '';
  private dateRangePicker?: flatpickr.Instance;
  private localizationService = new LocalizationService();

  constructor(
    public override _refreshService: RefreshService,
    private _printService: PrintService,
    public override _dialogService: DialogService,
    public override _entityRESTService: EntityRESTService,
    public override _utilityService: UtilityService,
    public override _fieldsService: FieldsService,
    public override _drupalRESTService: DrupalRESTService,
    public override _taxonomyService: TaxonomyService,
    public override _router: Router,
    public override _activatedRoute: ActivatedRoute,
    public override _authRESTService: AuthRESTService,
    public override _authService: AuthService,
    public override _menuRESTService: MenuRESTService,
    public override _formBuilder: FormBuilder,
    public override _routeEventsService: RouteEventsService,
    public override _permissionsService: PermissionsService,
    public override _renderer2: Renderer2,
    public override _configPages: ConfigPagesService,
    public override _packagesService: PackagesService,
    public override _snackBar: MatSnackBar,
    public override _userRESTService: UserService,
    public override _el: ElementRef,
    public override _changeDetectorRef: ChangeDetectorRef,
    public override _sanitizer: DomSanitizer,
    public override _studioConfigService: StudioConfigService,
    public override _componentRefreshService: ComponentRefreshService,
    public override _translateService: TranslateService,
    private currencyService: CurrencyService,
  ) {
    super(
      _refreshService,
      _dialogService,
      _entityRESTService,
      _utilityService,
      _fieldsService,
      _drupalRESTService,
      _taxonomyService,
      _router,
      _activatedRoute,
      _authRESTService,
      _authService,
      _menuRESTService,
      _formBuilder,
      _routeEventsService,
      _permissionsService,
      _renderer2,
      _configPages,
      _packagesService,
      _snackBar,
      _userRESTService,
      _el,
      _changeDetectorRef,
      _sanitizer,
      _studioConfigService,
      _componentRefreshService,
      _translateService
    );
  }

  override ngOnInit() {
    this.loadReport();
  }

  ngAfterViewInit(): void {
    this.initDateRangePicker();
  }

  // Studio config is app-lifetime; released on destroy so the report can be collected.
  private datePickerConfigSubscription: Subscription;

  override ngOnDestroy(): void {
    this.dateRangePicker?.destroy();
    this.datePickerConfigSubscription?.unsubscribe();
    super.ngOnDestroy();
  }

  private initDateRangePicker(): void {
    if (!this.dateRangeInput?.nativeElement) return;

    this.datePickerConfigSubscription?.unsubscribe();
    this.datePickerConfigSubscription = this._studioConfigService.getStudioConfig().subscribe(config => {
      const countryCode = config?.['field_studio_address']?.[0]?.country_code || 'US';
      const formats = this.localizationService.getDateFormatsForCountry(countryCode);

      this.dateRangePicker = flatpickr(this.dateRangeInput.nativeElement, {
        mode: 'range',
        dateFormat: formats.flatpickrDateFormat,
        disableMobile: true,
        defaultDate: this.dateTimeRange?.length === 2 ? this.dateTimeRange : undefined,
        onChange: (selectedDates) => {
          if (selectedDates.length === 2) {
            this.dateTimeRange = selectedDates;
            this.dateRangeDisplayValue = this.dateRangePicker?.input?.value || '';
            this.loadReport();
          }
        }
      });

      // Update display for default dates
      if (this.dateTimeRange?.length === 2 && this.dateRangePicker) {
        this.dateRangePicker.setDate(this.dateTimeRange, false);
        this.dateRangeDisplayValue = this.dateRangePicker.input.value;
      }
    });
  }

  openDateRangePicker(): void {
    this.dateRangePicker?.open();
  }

  /**
   * Query string for the report endpoint with every active filter applied.
   * Also used (with the /export suffix) by the CSV button so the download
   * always matches the screen.
   */
  private reportQuery(): string {
    const start = this.reportDateService.formatCalendarParam(this.dateTimeRange[0]);
    const end = this.reportDateService.formatCalendarParam(this.dateTimeRange[1]);
    let query = `start=${start}&end=${end}`;
    if (this.taxTypeFilter.length) {
      query += `&tax_type=${encodeURIComponent(this.taxTypeFilter.join(','))}`;
    }
    if (this.paymentTypeFilter.length) {
      query += `&payment_type=${this.paymentTypeFilter.join(',')}`;
    }
    if (this.statusFilter.length) {
      query += `&status=${encodeURIComponent(this.statusFilter.join(','))}`;
    }
    if (this.searchText.trim()) {
      query += `&search=${encodeURIComponent(this.searchText.trim())}`;
    }
    if (this.nonzeroTaxOnly) {
      query += `&nonzero_tax=1`;
    }
    if (this.minAmount != null && this.minAmount !== ('' as any)) {
      query += `&min_amount=${encodeURIComponent(String(this.minAmount))}`;
    }
    if (this.maxAmount != null && this.maxAmount !== ('' as any)) {
      query += `&max_amount=${encodeURIComponent(String(this.maxAmount))}`;
    }
    return query;
  }

  private studioId(): number | null {
    return this._authService.selectedStudio?.id ?? null;
  }

  /**
   * Human-readable chips for the filter summary panel. The panel renders
   * "parameter: value" verbatim, so pass display-ready labels and values.
   */
  private buildFilterChips(): { parameter: string; value: string }[] {
    const chips: { parameter: string; value: string }[] = [];
    chips.push({
      parameter: 'Date Range',
      value: `${this.reportDateService.formatDate(this.dateTimeRange[0])} – ${this.reportDateService.formatDate(this.dateTimeRange[1])}`,
    });
    if (this.taxTypeFilter.length) {
      chips.push({ parameter: 'Tax Type', value: this.taxTypeFilter.map(t => this.taxTypeLabel(t)).join(', ') });
    }
    if (this.paymentTypeFilter.length) {
      const names = this.availablePaymentTypes
        .filter(t => this.paymentTypeFilter.includes(t.id))
        .map(t => t.name);
      chips.push({ parameter: 'Payment Type', value: names.join(', ') });
    }
    if (this.statusFilter.length) {
      chips.push({ parameter: 'Status', value: this.statusFilter.join(', ') });
    }
    if (this.searchText.trim()) {
      chips.push({ parameter: 'Search', value: this.searchText.trim() });
    }
    if (this.nonzeroTaxOnly) {
      chips.push({ parameter: 'Tax Collected', value: 'over $0 only' });
    }
    if (this.minAmount != null || this.maxAmount != null) {
      const min = this.minAmount != null ? String(this.minAmount) : '';
      const max = this.maxAmount != null ? String(this.maxAmount) : '';
      chips.push({
        parameter: 'Gross Tuition',
        value: min && max ? `${min} – ${max}` : (min ? `at least ${min}` : `up to ${max}`),
      });
    }
    return chips;
  }

  exportCSV() {
    this.downloadCSVDrupalViews(`/api/sales-tax-report/${this.studioId()}/export?${this.reportQuery()}`, 'tax_report', []);
  }

  loadReport() {
    // Coalesce with a pending debounced search so Enter doesn't double-load.
    clearTimeout(this.searchDebounce);
    this.ShowProgressBar = true;
    this.queryParams = this.buildFilterChips();

    this._drupalRESTService.httpGET(`/api/sales-tax-report/${this.studioId()}?${this.reportQuery()}`, []).subscribe(
      (data) => {
        this.ShowProgressBar = false;
        this.dataSource = new MatTableDataSource(data['results']);
        this.availableTaxTypes = data['types'] || [];
        this.availablePaymentTypes = data['payment_types'] || [];
        this.availableStatuses = data['statuses'] || [];
        this.displayTypes = data['display_types'] || [];
        this.summary = data['summary'] || null;
        this.displayedColumns = [
          ...this.baseColumns,
          ...this.displayTypes.map(t => 'type_' + t),
          'field_total_payment_amount',
        ];
      },
      error => this.handleError(error)
    );
  }

  onFilterChange() {
    this.loadReport();
  }

  /**
   * Debounced search-as-you-type: reload shortly after typing pauses, so the
   * search works without knowing to press Enter (which still applies it
   * immediately via onFilterChange).
   */
  onSearchInput() {
    clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => this.loadReport(), 400);
  }
  private searchDebounce: ReturnType<typeof setTimeout>;

  taxTypeLabel(value: string): string {
    return getTaxTypeShortLabel(value, value);
  }

  typeTotal(type: string): number {
    return this.summary?.type_totals?.[type] ?? 0;
  }

  /**
   * One sentence covering every displayed type with rows that predate per-tax
   * tracking, e.g. "GST: 2, PST: 1 payment(s) predate per-tax tracking...".
   */
  untrackedNote(): string {
    if (!this.summary) return '';
    const parts = this.displayTypes
      .filter(t => (this.summary.type_untracked?.[t] ?? 0) > 0)
      .map(t => `${this.taxTypeLabel(t)}: ${this.summary.type_untracked[t]}`);
    return parts.length
      ? `${parts.join(', ')} payment(s) predate per-tax tracking and are not included in those totals.`
      : '';
  }

  override onSubmit(form: NgForm) {
    this.loadReport();
  }

  override resetForm() {
    this.dateTimeRange = [
      new Date(new Date().getFullYear(), 0, 1),
      new Date()
    ];
    this.dateRangePicker?.setDate(this.dateTimeRange, false);
    this.dateRangeDisplayValue = this.dateRangePicker?.input?.value || '';
    this.taxTypeFilter = [];
    this.paymentTypeFilter = [];
    this.statusFilter = [];
    this.searchText = '';
    this.nonzeroTaxOnly = false;
    this.minAmount = null;
    this.maxAmount = null;
    this.loadReport();
  }

  getFormattedDateRange(): string {
    if (this.dateTimeRange && this.dateTimeRange[0] && this.dateTimeRange[1]) {
      return `${this.reportDateService.formatDate(this.dateTimeRange[0])} - ${this.reportDateService.formatDate(this.dateTimeRange[1])}`;
    }
    return '';
  }

  override onPrint() {
    const rows: any[] = (this.dataSource?.data as any[]) || [];
    const money = (v: any) => this.currencyService.format(v ?? 0);

    let content = `
      <h2 class="report-title">Tax Report</h2>
      <p class="report-period">${this.getFormattedDateRange()}</p>
    `;

    content += `
      <div class="summary-section">
        <h3>Summary Totals</h3>
        <table class="summary-table">
          <tr>
            <td><strong>Total Tax Collected:</strong></td>
            <td>${money(this.summary?.total_tax_collected)}</td>
          </tr>
          ${this.displayTypes.map(t => `
          <tr>
            <td><strong>${this.taxTypeLabel(t)} Collected:</strong></td>
            <td>${money(this.typeTotal(t))}</td>
          </tr>`).join('')}
        </table>
        ${this.untrackedNote() ? `<p>${this.untrackedNote()}</p>` : ''}
      </div>
    `;

    content += `
      <table>
        <thead>
          <tr>
            <th>Title</th>
            <th>Date and Time</th>
            <th class="text-end">Gross Tuition</th>
            <th>Payment ID</th>
            <th>Payment Type</th>
            <th>Status</th>
            <th>Student Name</th>
            <th class="text-end">Tax Collected</th>
            ${this.displayTypes.map(t => `<th class="text-end">${this.taxTypeLabel(t)} Collected</th>`).join('')}
            <th class="text-end">Total Payment Amount</th>
          </tr>
        </thead>
        <tbody>
          ${rows.map(row => `
          <tr>
            <td>${row.title ?? ''}</td>
            <td>${this.reportDateService.formatDateTime(row.field_date_and_time)}</td>
            <td class="text-end">${money(row.field_gross_tuition)}</td>
            <td>${row.field_payment_id ?? ''}</td>
            <td>${row.field_payment_type ?? ''}</td>
            <td>${row.field_status ?? ''}</td>
            <td>${row.field_student_name ?? ''}</td>
            <td class="text-end">${money(row.field_tax_collected)}</td>
            ${this.displayTypes.map(t => `<td class="text-end">${row.tax_amounts?.[t] != null ? money(row.tax_amounts[t]) : '-'}</td>`).join('')}
            <td class="text-end">${money(row.field_total_payment_amount)}</td>
          </tr>`).join('')}
        </tbody>
        <tfoot>
          <tr class="total-row">
            <td><strong>Totals</strong></td>
            <td></td>
            <td class="text-end"><strong>${money(this.summary?.total_gross)}</strong></td>
            <td></td><td></td><td></td><td></td>
            <td class="text-end"><strong>${money(this.summary?.total_tax_collected)}</strong></td>
            ${this.displayTypes.map(t => `<td class="text-end"><strong>${money(this.typeTotal(t))}</strong></td>`).join('')}
            <td class="text-end"><strong>${money(this.summary?.total_payment_amount)}</strong></td>
          </tr>
        </tfoot>
      </table>
    `;

    const styles = `
      .report-title {
        font-size: 20px;
        text-align: center;
        margin: 5px 0;
      }
      .report-period {
        text-align: center;
        font-size: 13px;
        margin: 5px 0 10px 0;
      }
      .summary-section {
        margin: 10px 0;
        padding: 8px;
        background-color: #f8f9fa;
        border-radius: 3px;
        display: block;
      }
      .summary-section h3 {
        margin: 0 0 5px 0;
        font-size: 15px;
      }
      .summary-table {
        width: 100%;
        max-width: 400px;
        margin: 0 auto;
      }
      .summary-table td {
        padding: 3px 5px;
        border-bottom: 1px solid #ddd;
      }
      .text-end {
        text-align: right;
      }
      .total-row {
        border-top: 2px solid #333;
        font-weight: bold;
      }
      .total-row td {
        padding: 5px;
      }
    `;

    const title = `Tax Report - ${this.getFormattedDateRange()}`;

    this._printService.print(content, {
      title,
      styles,
      showSchoolName: true,
      promptPrint: true
    });
  }

}
